/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'underscore',
	'../../../widgets/livewidget/nls/StringResources',
	'../../../util/DashboardFormatter',
	'../../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'../topbottom/TopBottomLabel',
	'./FilterLabel'
], function(_, stringResources, Formatter, Class, TopBottomLabel, FilterLabel) {

	var FilterLabelHelper = null;
	FilterLabelHelper = Class.extend([FilterLabel, TopBottomLabel],{

		init: function(options) {
			FilterLabelHelper.inherited('init', this, arguments);
			this.dataSource = options.dataSource;
			this.visAPI = options.visAPI;
			this.timelineFilter = options.timelineFilter;
			this.filterLineEnabled = options.filterLineEnabled;
			this.filterLineDeleteEnabled = options.filterLineDeleteEnabled;
			this.iconsFeature = options.dashboardApi ? options.dashboardApi.getFeature('Icons') : this.visAPI.ownerWidget.dashboardApi.getFeature('Icons');
		},

		/**
		 * This function is used to format the top bottom filters in order to be printed out in the Filter Flyout.
		 */

		generateTopBottomListItems: function(topBottoms) {
			var aListItems = [];
			_.each(topBottoms, function(topBottom) {
				var line = {};
				var topBottomDescription = this._getTopBottomDescription(topBottom);
				if (topBottom.columnId !== undefined || topBottom.columnLabel !== undefined) {
					if (!topBottomDescription) {
						return;
					}
					line = {
						title: topBottom.columnLabel,
						description: topBottomDescription,
						toolTip: topBottomDescription
					};
				}
				if (topBottom.id) {
					line.deleteEnabled = topBottom.deleteEnabled;
					line.editEnabled = topBottom.editEnabled;
					line.dataId = topBottom.id;
					if (topBottom.limit.percent) {
						line.deleteLabel = stringResources.get('deleteTopBottomPercent');
						line.editLabel = stringResources.get('editTopBottomPercent');
						line.ariaDeleteLabel = stringResources.get('ariaDeleteTopBottomPercentLabel');
						line.ariaEditLabel = stringResources.get('ariaEditTopBottomPercentLabel');
					} else {
						line.deleteLabel = stringResources.get('deleteTopBottomCount');
						line.editLabel = stringResources.get('editTopBottomCount');
						line.ariaDeleteLabel = stringResources.get('ariaDeleteTopBottomCountLabel');
						line.ariaEditLabel = stringResources.get('ariaEditTopBottomCountLabel');
					}
					const topBottonIcon = this.iconsFeature.getIcon('dashboard-topbottom');
					line.svgHref = `#${topBottonIcon.id}`;
				}
				line.type = 'topBottom';
				aListItems.push(line);

			}.bind(this));
			return aListItems;
		},

		/**
		 * This function is used to format the filters in order to be printed out in the Filter Flyout.
		 */

		generateFilterListItems: function(filters) {
			var aListItems = [];
			_.each(filters, function(filter) {
				// Need metadata Information for formatting
				var line = {};
				var filterDesc = this.getFilterDescription(filter);
				if (filter.columnId !== undefined || filter.columnLabel !== undefined) {
					if (!filterDesc) {
						return;
					}
					line = {
						title: filter.columnLabel,
						description: filterDesc,
						toolTip: filterDesc
					};
				} else {
					line = {
						title: stringResources.get('dataPointFilterTitle'),
						description: filterDesc,
						toolTip: (filter.valueSummary) ? this.getDataPointFilterSummary(filter.valueSummary) : filterDesc
					};
				}
				if (filter.id) {
					//When deleteEnabled is set (currently for local filters only),  and the filter includes an id to delete, the filter can be deleted.
					const filterIcon = this.iconsFeature.getIcon('common-filter');
					line.dataId = filter.id;
					line.deleteLabel = stringResources.get('deleteFilter');
					line.editLabel = stringResources.get('editFilter');
					line.ariaDeleteLabel = stringResources.get('ariaDeleteFilterLabel');
					line.ariaEditLabel = stringResources.get('ariaEditFilterLabel');
					line.svgHref = `#${filterIcon.id}`;
					line.deleteEnabled = this._isFilterLineDeleteEnabled(filter);
					line.editEnabled = this._isFilterLineEnabled(filter);
				}
				line.type = this._getFilterLineType();
				aListItems.push(line);

			}.bind(this));
			return aListItems;
		},

		/**
		 * This function from the top bottom filter and column metadata constructs the label that tells the user which columns are being filtered.
		 */

		_getTopBottomDescription: function(topBottom) {
			if (topBottom.columnId !== undefined) {
				var columnMetaData = this.getColumnMetadataNameFromColumnId(topBottom.columnId);
				if (!columnMetaData) {
					return null;
				}
				topBottom.columnLabel = columnMetaData.getLabel();

				if (topBottom.limit && topBottom.limit.fact) {
					var factMetadata = this.getColumnMetadataNameFromColumnId(topBottom.limit.fact);
					if (factMetadata) {
						topBottom.limit.factColumnLabel = factMetadata.getLabel();
					}
				}
				return this.getTopBottomLabel(topBottom);
			}
		},

		/**
		 * This function from the filter object and the column metadata constructs the label that tells the user which columns are being filtered.
		 */

		getFilterDescription: function(filter) {
			if(filter.description){
				return filter.description;
			}else if (filter.columnId !== undefined) {
				var columnMetaData = this.dataSource.getMetadataColumn(filter.columnId);
				if (!columnMetaData) {
					return null;
				}
				return this.getFilterLabel(filter, columnMetaData);
			} else if (filter.columnMetadata) {
				//For the common viz control, the filter payload includes columnMetadata and the column label rather than a dataset
				//columnId.  In this case, we can return the label directly.
				return this.getFilterLabel(filter, filter.columnMetadata);
			}
			var filterInfo = (filter.operator === 'not') ? stringResources.get('dataPointFilterExcludeSummary', {
				valueCount: filter.valueCount
			}) :
				stringResources.get('dataPointFilterIncludeSummary', {
					valueCount: filter.valueCount
				});
			return filterInfo;
		},

		_isFilterLineDeleteEnabled: function (filter) {
			if(this.filterLineDeleteEnabled){
				return this.filterLineDeleteEnabled(filter);
			}
			return true;
		},

		_isFilterLineEnabled: function(filter){
			if(this.filterLineEnabled){
				return this.filterLineEnabled();
			}
			return (filter.columnId && filter.deleteEnabled && false !== filter.editEnabled && !filter.filterBins);
		},

		_getFilterLineType: function(){
			if(this.timelineFilter){
				return 'timelineFilter';
			}
			return 'filter';
		},

		getColumnMetadataNameFromColumnId: function(id) {
			if (id) {
				return this.dataSource.getMetadataColumn(id);
			}
		},

		getPromptLabel: function(oPromptInfo) {
			let aFormattedValues = [];
			// TODO cleaning once useApi
			let propertyType = oPromptInfo.values && oPromptInfo.values.length && oPromptInfo.values[0].d !== undefined ? 'd' : 'label';
			let aDisplayValues = _.pluck(oPromptInfo.values, propertyType);
			let sFormatType;
			let isAttributeColumn;
			switch (oPromptInfo.dataType) {
				case 'xsdDate':
				case 'xsdDateTime':
				case 'xsdTime':
					sFormatType = oPromptInfo.dataType === 'xsdDate' ? 'date' : oPromptInfo.dataType === 'xsdDateTime' ? 'datetime' : 'time';
					aDisplayValues.forEach(sValue => {
						aFormattedValues.push(Formatter.format(sValue,{
							type: sFormatType,
							formatLength: 'medium'
						}));
					});
					break;
				case 'xsdInt':
				case 'xsdLong':
				case 'xsdShort':
				case 'xsdFloat':
				case 'xsdDecimal':
				case 'xsdDouble':
					isAttributeColumn = oPromptInfo.modelFilterItem && this.getColumnMetadataNameFromColumnId(oPromptInfo.modelFilterItem).getType() === 'attribute';
					// Only format numeric data type when it's not an attribute column.
					if (!isAttributeColumn) {
						aDisplayValues.forEach(sValue => {
							aFormattedValues.push(Formatter.format(sValue, {
								type: 'number'
							}));
						});
					} else {
						return aDisplayValues.join(stringResources.get('listSeparator') + ' ');
					}
					break;
				default:
					return aDisplayValues.join(stringResources.get('listSeparator') + ' ');
			}

			var result;
			switch (oPromptInfo.promptModuleName) {
				case 'range':
					result = this.getRangePromptLabel(aFormattedValues);
					break;
				default:
					result = aFormattedValues.join(stringResources.get('listSeparator') + ' ');
			}
			return result;
		},

		getRangePromptLabel: function(aFormattedValues) {
			return stringResources.get('measureFilterBetween', {
				lowerBound: aFormattedValues[0],
				upperBound: aFormattedValues[1]
			});
		}

	});

	return FilterLabelHelper;
});
